import {
  type InfiniteData,
  type QueryFunctionContext,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
} from '@tanstack/react-query';

import { teamService } from '@/services/team-service';
import type { Team } from '@/types/core/team';
import type { Page } from '@/types/shared/page';
import { isEmptyUUID, type UUID } from '@/utils/uuid';

import { teamQueryKeys } from './query-keys';

/**
 * List teams for a given tenant.
 * Accepts optional paging args via `opts`.
 */
export function useListTeams(
  tenantId: UUID,
  opts?: {
    limit?: number;
    next?: string;
    filter?: string;
  }
): UseInfiniteQueryResult<InfiniteData<Page<Team>, unknown>, Error> {
  return useInfiniteQuery<Page<Team>, Error, InfiniteData<Page<Team>, unknown>>(
    {
      queryKey: teamQueryKeys.list(tenantId, opts?.limit, opts?.filter),
      queryFn: (context: QueryFunctionContext) => {
        const pageParam = context.pageParam as string | undefined;
        return teamService.listTeams(tenantId, {
          limit: opts?.limit,
          next: pageParam,
          filter: opts?.filter,
        });
      },
      enabled: !isEmptyUUID(tenantId),
      initialPageParam: opts?.next ?? undefined,
      getNextPageParam: (last) => last.next ?? undefined,
      getPreviousPageParam: (first) => first.prev ?? undefined,
    }
  );
}
